import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import { copyToClipboard } from './paymentFormUtils';

interface InlineCopyLinkProps {
  link: string;
  emptyMessage?: string;
  maxWidth?: number | 'none';
  fullWidth?: boolean;
}

const InlineCopyLink: React.FC<InlineCopyLinkProps> = ({
  link,
  emptyMessage = 'Salve o evento para gerar o link',
  maxWidth = 300,
  fullWidth = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!link) return;
    await copyToClipboard(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!link) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        width: fullWidth ? '100%' : 'auto',
        maxWidth: '100%',
        px: 1,
        py: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: copied ? 'success.main' : 'divider',
        bgcolor: copied ? 'action.selected' : 'grey.50',
        transition: 'border-color 0.2s, background-color 0.2s',
      }}
    >
      <LinkIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
      <Tooltip title={link} placement="top" enterDelay={400}>
        <Typography
          component="button"
          type="button"
          onClick={handleCopy}
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            color: 'text.primary',
            border: 'none',
            background: 'none',
            p: 0,
            m: 0,
            cursor: 'pointer',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: fullWidth ? 1 : undefined,
            minWidth: 0,
            maxWidth: maxWidth === 'none' ? 'none' : maxWidth,
            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
          }}
        >
          {link}
        </Typography>
      </Tooltip>
      <Tooltip title={copied ? 'Copiado!' : 'Copiar link'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          aria-label="Copiar link"
          sx={{
            ml: 0.25,
            width: 28,
            height: 28,
            color: copied ? 'success.main' : 'primary.main',
          }}
        >
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default InlineCopyLink;
